import Model from "../../model/Model";
import Review, {ReviewStatus} from "../../model/Review";
import User from "../../model/User";
import * as React from "react";
import {ReactElement, useEffect, useState} from "react";
import {useResources} from "../../stores/ResourceProvider";
import {
  Avatar,
  Box,
  Checkbox,
  DialogContentText,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  useTheme
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import FormValidator from "../../components/form/FormValidator";
import DialogContent from "@mui/material/DialogContent";
import ClientMenu from "../../components/controls/ClientMenu";
import DatePickerValidator from "../../components/form/DatePickerValidator";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import NavListItem from "../../components/controls/NavListItem";
import DialogActions from "@mui/material/DialogActions";
import CancelButton from "../../components/form/CancelButton";
import ProgressButton from "../../components/form/ProgressButton";
import TextFieldValidator from "../../components/form/TextFieldValidator";
import Snapshot from "../../model/Snapshot";
import {createUUID, dateToLocalFormat, getISODateFromDate} from "../../stores/StoreUtilities";
import config from 'react-global-configuration';
import {CopyToClipboard} from 'react-copy-to-clipboard';
import {
  ClientAccess,
  CreateReviewInput,
  CreateSnapshotInput,
  SnapshotType,
  UpdateModelInput,
  UserType
} from "../../API";
import {isSameDay} from "date-fns";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const NewReviewDialog = ({
  user,
  onChange,
  onClose,
  onCreate
}: {
  user?: User,
  onChange(client: User): void
  onClose(): void
  onCreate(review: Review): void
}) => {
  const [client, setClient] = useState<User | undefined>()
  const [models, setModels] = useState<Model[]>([])
  const [baseModel, setBaseModel] = useState<Model | undefined>()
  const [reviewDate, setReviewDate] = useState<Date | undefined>(new Date())
  const [modelName, setModelName] = useState<string>("")
  const [copyModel, setCopyModel] = useState<boolean>(true)
  const [snapshots, setSnapshots] = useState<Snapshot[]>([])
  const [baseSnapshot, setBaseSnapshot] = useState<Snapshot | undefined>()
  const id = createUUID()
  const [managed, setManaged] = useState<boolean>(false)
  const [reviewId, setReviewId] = useState<string>(id)
  const [reviewLink, setRevierwLink] =
    useState<string | undefined>(`${config.get("baseUrl")}/review/${id}`)
  const [expandClientSearch, setExpandClientSearch] = useState<boolean>(false)
  const [message, setMessage] = useState<string | undefined>()
  const [isProcessing, setIsProcessing] = useState<boolean>(false)

  const {modelStore, userStore, notify} = useResources()
  const theme = useTheme()
  
  const styles = {
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "1.0em",
      maxHeight: 15,
      marginBottom: "6px",
      textWrap: "nowrap",
      color: theme.palette.grey["700"]
    },
  }

  useEffect(() => {
    if (user) {
      loadClient(user)
    }
  }, [user])
  
  const loadClient = async (user?: User) => {
    if (!user) {
      return
    }
    if (user.models.length === 0) {
      user = await userStore.getUser(user.id)
      if (!user) {
        return
      }
    }
    setClient(user)
    setModels(user.models.filter((m: Model) => !m.archived).sort((a: Model, b: Model) => a.name.localeCompare(b.name)))

    let primaryModel: Model | undefined
    if (user.advisorPrimaryModelId) {
      primaryModel = user!.models.find((m: Model) => m.id === user!.advisorPrimaryModelId)
      if (primaryModel) {
        loadModel(primaryModel)
      }
    }

    if (user.userType === UserType.Client || user.isAdvisor) {
      setManaged(true)
    }
  }

  const loadModel = async (model: Model) => {
    const fullModel = await modelStore.getCurrentModel(model.id, true)
    if (fullModel) {
      setBaseModel(fullModel)
      setSnapshots(fullModel.snapshots)
      if (fullModel.snapshots.length > 0) {
        setBaseSnapshot(fullModel.snapshots[0])
      }
    }
  }

  const handleSubmit = async () => {
    if (!client || !baseModel || !baseSnapshot || !reviewDate) {
      return
    }

    try {
      setMessage(undefined)
      setIsProcessing(true)

      let reviewModel: Model | undefined
      let baseSnap: Snapshot | undefined
      let reviewSnapshot: Snapshot | undefined

      // See if a review with this date already exists
      const findDate = getISODateFromDate(reviewDate)
      const review = client.reviews.find((r: Review) => r.reviewDate === findDate )
      if (review) {
        setMessage("A review with this date already exists.")
        return
      }

      if (copyModel && baseModel) {
        // See if model name already exists
        const findName = modelName.toLowerCase()
        const found = client.models.find((m: Model) => m.name.toLowerCase().localeCompare(findName) === 0)
        if (found) {
          setMessage(`A model with the name "${modelName}" already exists.`)
          return
        }
        reviewModel = await modelStore.newModel(modelName, "", baseModel, true, ClientAccess.None)
        if (reviewModel) {
          modelStore.setCurrentModel(reviewModel)
          // Find copied snapshot
          baseSnap = reviewModel.snapshots.find((s: Snapshot) => isSameDay(s.date, baseSnapshot.date))
          if (baseSnap) {
            baseSnap = await modelStore.updateSnapshotBudget(baseSnap, reviewModel)
            if (baseSnap) {
              // Get latest details
              const details = modelStore.buildSnapshotDetails(reviewModel, reviewDate, SnapshotType.Combined)
              const snapshotInput: CreateSnapshotInput = {
                userId: client.id,
                accountId: client.accountId,
                modelId: reviewModel.id,
                date: getISODateFromDate(reviewDate),
                description: `Annual Review ${reviewDate.getFullYear()}`,
                snapshotType: SnapshotType.Combined,
                details: JSON.stringify(details)
              }
              reviewSnapshot = await modelStore.createSnapshot(snapshotInput)
            }
          }
        }
      } else {
        reviewModel = baseModel
        modelStore.setCurrentModel(reviewModel)
        // Find copied snapshot
        baseSnap = reviewModel.snapshots.find((s: Snapshot) => isSameDay(s.date, baseSnapshot.date))
        if (baseSnap) {
          baseSnap = await modelStore.updateSnapshotBudget(baseSnap, reviewModel)
          if (baseSnap) {
            // Look for existing snapshot on the review date
            reviewSnapshot = reviewModel.snapshots.find((s: Snapshot) => isSameDay(s.date, reviewDate))
            if (!reviewSnapshot) {
              // Get latest details
              const details = modelStore.buildSnapshotDetails(reviewModel, reviewDate, SnapshotType.Combined)
              const snapshotInput: CreateSnapshotInput = {
                userId: client.id,
                accountId: client.accountId,
                modelId: baseModel.id,
                date: getISODateFromDate(reviewDate),
                description: `Annual Review ${reviewDate.getFullYear()}`,
                snapshotType: SnapshotType.Combined,
                details: JSON.stringify(details)
              }
              reviewSnapshot = await modelStore.createSnapshot(snapshotInput)
            }
          }
        }
        if (reviewModel.clientAccess !== ClientAccess.None) {
          // Set clientAccess to None
          const update: UpdateModelInput = {
            id: reviewModel.id,
            clientAccess: ClientAccess.None
          }

          reviewModel = await modelStore.updateModel(update)
        }
      }

      if (reviewModel && baseSnap && reviewSnapshot) {
        const input: CreateReviewInput = {
          id: reviewId,
          userId: client.id,
          ownerId: client.id,
          accountId: client.accountId,
          modelId: reviewModel.id,
          baseSnapId: baseSnap.id,
          snapshotId: reviewSnapshot.id,
          reviewDate: getISODateFromDate(reviewDate),
          checklist: JSON.stringify(Review.getDefaultChecklist(managed)),
          managed: managed,
          status: ReviewStatus.Pending
        }

        const review = await userStore.createReview(input)
        if (review) {
          onCreate(review)
        }
      }
    } catch (err: any) {
      notify.show("error", `Error creating new review (${err.message})`)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleChangeClient = (user: User) => {
    onChange(user)
  }

  const onCopyLink = () => {
    notify!.show('success', 'Copied Review link to clipboard.')
  }

  const renderAvatar = () => {
    let avatar: ReactElement | undefined
    if (client) {
      const bgcolor = (client.id === userStore.user?.id) ? theme.palette.primary.main : theme.palette.action.active
      avatar = <Avatar sx={{bgcolor: bgcolor, fontSize:13, width:25, height:25}}>
        {client.initials}
      </Avatar>
    } else {
      avatar = <ManageAccountsIcon/>
    }
    return avatar
  }

  return (
    <Dialog
      id="newReviewDialog"
      open={true}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="newReviewDialogTitle"
    >
      <FormValidator onSubmit={handleSubmit} autoComplete="off">
        <DialogTitle id="newReviewDialogTitle">Create Review</DialogTitle>
        <DialogContent sx={{my:2}}>
          <DialogContentText>
            Create a review using a base model and snapshot from the prior year.
            You may either have it create a new model and snapshot or just a new snapshot in the existing base model.
          </DialogContentText>
          <Grid container spacing={1} mt={1}>
              <Grid item xs={8}>
                <FormLabel sx={styles.formLabel}>Client</FormLabel>
                <NavListItem id="client"
                             icon={renderAvatar()}
                             title={`Client`}
                             primary={client ? client.fullName : "Select Client"}
                             expanded={expandClientSearch}
                             pl={0}
                             divider={true}
                             onClick={() => {
                               setExpandClientSearch(!expandClientSearch)
                             }}
                             onChange={(expanded: boolean) => setExpandClientSearch(expanded)}
                             drawerOpen={true}
                >
                    <ClientMenu color="primary" client={client}
                                onChangeClient={(client: User) => {
                                  handleChangeClient(client)
                                  setExpandClientSearch(false)
                                }}
                    />
                </NavListItem>
              </Grid>
              <Grid item xs={4}>
                <DatePickerValidator
                  name="reviewDate"
                  label="Review Date"
                  value={reviewDate}
                  format="MM-dd-yyyy"
                  variant="standard"
                  margin="dense"
                  fullWidth
                  required
                  onChange={(newValue: any) => {
                    setReviewDate(newValue)
                  }}
                />
              </Grid>
              <Grid item xs={8}>
                <TextFieldValidator
                  type="text"
                  validators={{ required: true }}
                  name="baseModel"
                  label="Base Model"
                  variant="standard"
                  autocompleteOptions={{
                    freeSolo: false,
                    options: models,
                    getOptionLabel: (option: Model) => option && option.name ? option.name : "None",
                    isOptionEqualToValue: (option: Model, value: Model) => {
                      return (option.id === value.id)
                    },
                    value: baseModel ?? null,
                    onChange: (event: any, value: Model, reason: any) => {
                      loadModel(value)
                    }
                  }}
                />
              </Grid>
              <Grid item xs={4} sx={{display:"flex", flexDirection:"column", justify:"bottom"}}>
                <TextFieldValidator
                  type="text"
                  validators={{ required: true }}
                  name="baseSnapshot"
                  label="Base Snapshot"
                  variant="standard"
                  autocompleteOptions={{
                    freeSolo: false,
                    options: snapshots,
                    getOptionLabel: (option: Snapshot) => option && option.date ? `${dateToLocalFormat(option.date)}${option.snapshotType === SnapshotType.Combined ? "*" : ""}` : "None",
                    isOptionEqualToValue: (option: Snapshot, value: Snapshot) => {
                      return (option.id === value.id)
                    },
                    value: baseSnapshot ?? null,
                    onChange: (event: any, value: Snapshot, reason: any) => {
                      setBaseSnapshot(value)
                    }
                  }}
                />
              </Grid>
              <Grid item xs={8}>
                <TextFieldValidator
                  margin="dense"
                  name="name"
                  label="New Model Name"
                  type="text"
                  validators={{required: copyModel}}
                  value={modelName}
                  disabled={!copyModel}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setModelName(event.target.value)}
                  fullWidth
                  variant="standard"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl sx={{paddingTop:"20px"}}>
                  <FormControlLabel
                    control={
                      <Checkbox name="copyModel" value={true}
                                checked={copyModel}
                                onClick={(event: any) => setCopyModel(event.target.checked)}
                      />}
                    label="Copy Model"
                    labelPlacement="end"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextFieldValidator
                  margin="dense"
                  name="reviewLink"
                  label="Review Link"
                  type="text"
                  validators={{required:true}}
                  value={reviewLink}
                  disabled={true}
                  fullWidth
                  variant="standard"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CopyToClipboard
                          text={reviewLink ?? ""}
                          onCopy={onCopyLink}>
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => {/* TODO */}}
                          >
                            <ContentCopyIcon/>
                          </IconButton>
                        </CopyToClipboard>
                      </InputAdornment>
                    )
                  }}
                />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={
                <Switch checked={managed}
                        onChange={(event: any) => {
                          setManaged(event.target.checked)
                        }}
                />}
                label="Hide Managed Assets from Clients"
              />
            </Grid>
          </Grid>
          {message &&
            <DialogContentText sx={{color: theme.palette.error.main, mt: 1}}>
              {message}
            </DialogContentText>
          }
        </DialogContent>
        <DialogActions>
          <Box display="flex" justifyContent="flex-end">
            <CancelButton onClick={onClose} />
            <ProgressButton variant="contained" size="medium" color="primary" sx={{ color: "#fff" }}
                            type="submit" processing={isProcessing}>
              Create
            </ProgressButton>
          </Box>
        </DialogActions>
      </FormValidator>
    </Dialog>
  )
}

export default NewReviewDialog